import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { getCrmKindStats, type CrmKindStats as KindStats, type LeadKind } from '../api/crm';
import { stagesForSlug } from './crm-stages';
import { INZERENT_SERVICES, langDef } from './crm-lang';
import { money, moneyAlt } from './crm-money';
import { CompactSelect } from '../components/arc/compact-select/compact-select';
import './crm-stats.css';

const nf = new Intl.NumberFormat('cs-CZ');

const KIND_COLOR: Record<LeadKind, string> = { affiliate: '#188574', inzerent: '#6544a5' };

const FUNNEL_LABELS: Record<string, string> = {
  received: 'Lead přijat',
  intro_call: 'Úvodní call',
  offer: 'Nabídka odeslána',
  contract: 'Smlouva odeslána',
  client: 'Získán jako klient',
  email_confirmed: 'E-mail ověřen',
  registration_sent: 'Registrace odeslána',
  registration_opened: 'Registrace otevřena',
  converted: 'Získán jako partner',
  paired: 'Spárován s platformou',
};

function monthLabel(ym: string): string {
  const [y = 0, m = 1] = ym.split('-').map(Number);
  const s = new Date(y, m - 1, 1).toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function lastMonths(n: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function share(count: number, total: number): number {
  return total ? Math.round((count * 1000) / total) / 10 : 0;
}

function pct(value: number): string {
  return `${value.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} %`;
}

function BarCards({ items, total, color }: { items: { key: string; label: string; count: number; note?: string }[]; total: number; color: string }) {
  return (
    <div className="crm-stats-stages">
      {items.map((item) => {
        const s = share(item.count, total);
        return (
          <article key={item.key}>
            <div><span><i style={{ background: color }} />{item.label}</span><strong>{nf.format(item.count)}</strong></div>
            <div className="crm-stats-stage-track"><i style={{ width: `${Math.min(100, s)}%`, background: color }} /></div>
            <small>{item.note ?? `${pct(s)} leadů`}</small>
          </article>
        );
      })}
    </div>
  );
}

export function CrmKindStats({ spaceSlug, onClose }: { spaceSlug: string | null; onClose: () => void }) {
  const [kind, setKind] = useState<LeadKind>('inzerent');
  const [period, setPeriod] = useState<'12' | '24' | 'all'>('12');
  const [data, setData] = useState<KindStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stages = stagesForSlug(spaceSlug);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    getCrmKindStats(kind, period === 'all' ? 'all' : Number(period))
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Statistiky se nepodařilo načíst'); });
    return () => { cancelled = true; };
  }, [kind, period]);

  const isInzerent = kind === 'inzerent';
  const color = KIND_COLOR[kind];
  const total = data?.total ?? 0;
  const rate = data && total ? Math.round((data.confirmed * 100) / total) : 0;
  const paired = data?.funnel.find((f) => f.key === 'paired')?.count ?? 0;
  const monthKeys = data ? (period === 'all' ? data.months.map((m) => m.ym) : lastMonths(Number(period))) : [];
  const monthRows = data
    ? monthKeys.map((ym) => data.months.find((m) => m.ym === ym) ?? { ym, total: 0, confirmed: 0, value: 0, confirmedValue: 0 })
    : [];
  const maxMonth = Math.max(1, ...monthRows.map((m) => m.total));

  const funnelItems = (data?.funnel ?? []).map((f) => ({ key: f.key, label: FUNNEL_LABELS[f.key] ?? f.key, count: f.count }));
  const productItems = (data?.products ?? [])
    .filter((p) => p.key !== '' || p.count > 0)
    .map((p) => ({
      key: p.key || 'none',
      label: p.key ? (INZERENT_SERVICES.find((s) => s.value === p.key)?.label ?? p.key) : 'Bez produktu',
      count: p.count,
      note: p.value ? `${money(p.value, spaceSlug)} za měsíc${moneyAlt(p.value, spaceSlug) ? ` · ${moneyAlt(p.value, spaceSlug)}` : ''}` : 'bez hodnoty',
    }));
  const focusItems = (data?.focus ?? []).map((f) => ({ key: f.key || 'none', label: f.key || 'Bez zaměření', count: f.count }));
  const langItems = (data?.langs ?? []).map((l) => {
    const def = langDef(l.key);
    return { key: l.key || 'none', label: def ? `${def.flag} ${def.fullName}` : (l.key || 'Bez jazyka'), count: l.count };
  });

  return (
    <div className="crm-page crm-stats">
      <div className="crm-stats-top">
        <div>
          <button type="button" className="crm-stats-back" onClick={onClose}>Zpět na leady</button>
          <span className="crm-stats-eyebrow">CRM · VÝKON</span>
          <h2 className="crm-stats-title">{isInzerent ? 'Statistiky inzerentů' : 'Statistiky affiliate'}</h2>
          <p>{isInzerent ? 'Obchodní příležitosti, postup k uzavření smlouvy a předpoklad příjmů.' : 'Zájemci o partnerství, registrace a získaní partneři.'}</p>
        </div>
        <div className="crm-stats-controls">
          <div className="crm-stats-kind-toggle" role="tablist" aria-label="Typ leadu">
            <button type="button" role="tab" aria-selected={isInzerent} className={isInzerent ? 'active inz' : 'inz'} onClick={() => setKind('inzerent')}>Inzerent</button>
            <button type="button" role="tab" aria-selected={!isInzerent} className={!isInzerent ? 'active aff' : 'aff'} onClick={() => setKind('affiliate')}>Affiliate</button>
          </div>
          <div className="crm-stats-period"><span>Období</span><CompactSelect
            value={period}
            options={[{ value: '12', label: 'Posledních 12 měsíců' }, { value: '24', label: 'Posledních 24 měsíců' }, { value: 'all', label: 'Celá historie' }]}
            onChange={(value) => setPeriod(value as '12' | '24' | 'all')}
            ariaLabel="Období"
          /></div>
        </div>
      </div>

      {error && <div className="crm-stats-message is-error">{error}</div>}
      {!data && !error && <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /></div>}

      {data && (
        <>
          <section className="crm-stats-overview" aria-label="Souhrn statistik">
            <article className="crm-stats-volume">
              <span>{isInzerent ? 'Nové příležitosti' : 'Noví zájemci'}</span>
              <strong>{nf.format(total)}</strong>
              <small>{isInzerent ? 'inzerentských leadů v období' : 'affiliate leadů v období'}</small>
            </article>
            <article className="crm-stats-conversion">
              <span>Úspěšnost</span>
              <div><strong>{nf.format(data.confirmed)}</strong><em>{rate} %</em></div>
              <small>{isInzerent ? 'potvrzených leadů' : 'získaných partnerů'}</small>
              <div className="crm-stats-conversion-track"><i style={{ width: `${Math.min(100, rate)}%` }} /></div>
            </article>
            {isInzerent ? (
              <article className="crm-stats-values">
                <span>Příjmy za měsíc</span>
                <div><small>Předpoklad, na stole</small><strong>{money(data.tableValue, spaceSlug)}</strong>{moneyAlt(data.tableValue, spaceSlug) ? <em>{moneyAlt(data.tableValue, spaceSlug)}</em> : null}</div>
                <div className="is-won"><small>Potvrzené</small><strong>{money(data.confirmedValue, spaceSlug)}</strong><em>{moneyAlt(data.confirmedValue, spaceSlug) ? `${moneyAlt(data.confirmedValue, spaceSlug)} · ` : ''}≈ {money(data.confirmedValue * 12, spaceSlug)} ročně</em></div>
              </article>
            ) : (
              <article className="crm-stats-values">
                <span>Výsledek</span>
                <div><small>Neschválení</small><strong>{nf.format(data.rejected)}</strong><em>{pct(share(data.rejected, total))} zájemců</em></div>
                <div className="is-won"><small>Spárovaní s platformou</small><strong>{nf.format(paired)}</strong><em>{pct(share(paired, total))} zájemců</em></div>
              </article>
            )}
          </section>

          <section className="crm-stats-panel">
            <header><div><span>Postup</span><h2>{isInzerent ? 'Kam se leady dostaly' : 'Kam se zájemci dostali'}</h2><p>Počet leadů, které daným krokem prošly.</p></div><small>{nf.format(total)} celkem{data.rejected ? ` · ${nf.format(data.rejected)} neschváleno` : ''}</small></header>
            <BarCards items={funnelItems} total={total} color={color} />
          </section>

          <section className="crm-stats-panel crm-stats-stage-panel">
            <header><div><span>Pipeline</span><h2>Leady podle fáze</h2></div><small>{nf.format(total)} celkem</small></header>
            <div className="crm-stats-stages">
              {stages.map((s) => {
                const n = data.byStatus[s.key] ?? 0;
                return (
                  <article key={s.key}>
                    <div><span><i style={{ background: s.color }} />{s.label}</span><strong>{nf.format(n)}</strong></div>
                    <div className="crm-stats-stage-track"><i style={{ width: `${share(n, total)}%`, background: s.color }} /></div>
                    <small>{pct(share(n, total))} leadů</small>
                  </article>
                );
              })}
            </div>
          </section>

          {isInzerent ? (
            <section className="crm-stats-panel">
              <header><div><span>Produkty</span><h2>Leady podle produktu</h2><p>Lead s více produkty se počítá u každého z nich.</p></div></header>
              <BarCards items={productItems} total={total} color={color} />
            </section>
          ) : (
            <>
              <section className="crm-stats-panel">
                <header><div><span>Zaměření</span><h2>Zájemci podle zaměření</h2></div></header>
                {focusItems.length ? <BarCards items={focusItems} total={total} color={color} /> : <div className="crm-stats-empty">Zatím žádné leady.</div>}
              </section>
              <section className="crm-stats-panel">
                <header><div><span>Trhy</span><h2>Zájemci podle jazyka</h2></div></header>
                {langItems.length ? <BarCards items={langItems} total={total} color={color} /> : <div className="crm-stats-empty">Zatím žádné leady.</div>}
              </section>
            </>
          )}

          <section className="crm-stats-panel crm-stats-month-panel">
            <header><div><span>Vývoj</span><h2>Podle měsíce</h2><p>{isInzerent ? 'Leady a hodnota podle měsíce přijetí.' : 'Zájemci podle měsíce přijetí.'}</p></div><small>{period === 'all' ? 'Celá historie' : `${period} měsíců`}</small></header>
            {monthRows.length === 0 ? (
              <div className="crm-stats-empty">Zatím žádné leady.</div>
            ) : (
              <div className="crm-stats-table-wrap">
                <table className="crm-stats-table">
                  <thead>
                    <tr>
                      <th>Měsíc</th>
                      <th>Leady</th>
                      <th className="num">{isInzerent ? 'Potvrzené' : 'Získaní'}</th>
                      {isInzerent ? <th className="num">Hodnota / měsíc</th> : null}
                      {isInzerent ? <th className="num">Potvrzená hodnota</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {monthRows.map((m) => (
                      <tr key={m.ym} className={m.total ? '' : 'is-empty'}>
                        <td className="crm-stats-month" data-label="Měsíc"><strong>{monthLabel(m.ym)}</strong></td>
                        <td className="crm-stats-month-volume" data-label="Leady">
                          <div><span className="crm-stats-bar"><i className={isInzerent ? 'inz' : 'aff'} style={{ width: `${(m.total * 100) / maxMonth}%` }} /></span><b>{nf.format(m.total)}</b></div>
                        </td>
                        <td className="num crm-stats-month-won" data-label={isInzerent ? 'Potvrzené' : 'Získaní'}><strong>{nf.format(m.confirmed)}</strong><small>{m.total ? `${Math.round((m.confirmed * 100) / m.total)} %` : '—'}</small></td>
                        {isInzerent ? <td className="num crm-stats-money" data-label="Hodnota / měsíc"><strong>{m.value ? money(m.value, spaceSlug) : '—'}</strong><small>{m.value ? (moneyAlt(m.value, spaceSlug) ?? 'pipeline') : 'bez hodnoty'}</small></td> : null}
                        {isInzerent ? <td className="num crm-stats-money is-won" data-label="Potvrzená hodnota"><strong>{m.confirmedValue ? money(m.confirmedValue, spaceSlug) : '—'}</strong><small>{m.confirmedValue ? (moneyAlt(m.confirmedValue, spaceSlug) ?? 'měsíčně') : 'bez hodnoty'}</small></td> : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
